import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { FileCheck2, Building2, Settings, CheckCircle2, XCircle, AlertTriangle, ShieldCheck, Eye, EyeOff, Loader2 } from "lucide-react";
import { ImpresaModal } from "./DicoBlock";

const StatusRow = ({ ok, label }) => (
  <div className="flex items-center gap-2 text-xs">
    {ok
      ? <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "#059669" }} />
      : <XCircle className="w-3.5 h-3.5" style={{ color: "#94a3b8" }} />}
    <span style={{ color: ok ? "var(--text)" : "var(--text-muted)" }}>{label}</span>
  </div>
);

/**
 * Card riassuntiva per la sezione Impostazioni:
 * mostra lo stato di compilazione dei dati impresa DiCo (globali) e la
 * scadenza della visura camerale. Apre il modale ImpresaModal riusato dal
 * DicoBlock del cliente, così la configurazione avviene in un solo posto.
 */
export default function DicoImpresaConfig() {
  const [impresa, setImpresa] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = async () => {
    try {
      const [imp, al] = await Promise.all([
        api.get("/dico/impresa"),
        api.get("/dico/impresa/alerts"),
      ]);
      setImpresa(imp.data);
      setAlerts(al.data);
    } catch {
      setImpresa({});
      setAlerts(null);
    }
  };
  useEffect(() => { load(); }, []);

  const has = (k) => !!(impresa && String(impresa[k] || "").trim());

  const visuraBadge = () => {
    if (!alerts || !alerts.has_visura) return null;
    if (alerts.is_expired) {
      return (
        <div className="text-xs px-2 py-1 rounded-full font-bold inline-flex items-center gap-1" style={{ background: "#fef2f2", color: "#991b1b", border: "1px solid #fca5a5" }} data-testid="imp-config-visura-expired">
          <AlertTriangle className="w-3 h-3" /> Visura scaduta da {Math.abs(alerts.days_left)} giorni
        </div>
      );
    }
    if (alerts.is_expiring) {
      return (
        <div className="text-xs px-2 py-1 rounded-full font-bold inline-flex items-center gap-1" style={{ background: "#fffbeb", color: "#92400e", border: "1px solid #fcd34d" }} data-testid="imp-config-visura-expiring">
          <AlertTriangle className="w-3 h-3" /> Visura in scadenza tra {alerts.days_left} gg
        </div>
      );
    }
    return (
      <div className="text-xs px-2 py-1 rounded-full inline-flex items-center gap-1" style={{ background: "#ecfdf5", color: "#065f46", border: "1px solid #a7f3d0" }} data-testid="imp-config-visura-ok">
        <ShieldCheck className="w-3 h-3" /> Visura valida ({alerts.days_left} gg)
      </div>
    );
  };

  return (
    <div className="card-shell p-5" data-testid="dico-impresa-config">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <FileCheck2 className="w-5 h-5" style={{ color: "var(--primary)" }} />
            <h3 className="font-bold text-base" style={{ fontFamily: "Manrope" }}>DiCo — Dati impresa (globali)</h3>
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
            Anagrafica, logo, firme, timbri e visura camerale. Configurati una sola volta, usati automaticamente in tutte le Dichiarazioni di Conformità di tutti i clienti.
          </div>
        </div>
        <div className="flex items-center gap-2">
          {visuraBadge()}
          <button onClick={() => setModalOpen(true)} className="btn-primary text-sm" data-testid="dico-impresa-config-open">
            <Settings className="w-4 h-4" /> Apri configurazione
          </button>
        </div>
      </div>

      {impresa && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 pt-3 border-t" style={{ borderColor: "var(--border)" }}>
          <StatusRow ok={has("ragione_sociale") && has("piva")} label="Anagrafica (Ragione sociale + P.IVA)" />
          <StatusRow ok={has("sede_indirizzo") && has("sede_comune")} label="Sede legale" />
          <StatusRow ok={has("responsabile_tecnico")} label="Responsabile Tecnico" />
          <StatusRow ok={has("titolare")} label="Legale rappresentante" />
          <StatusRow ok={has("pec") || has("email")} label="Contatti PEC/Email" />
          <StatusRow ok={has("sdi")} label="Codice SDI" />
          <StatusRow ok={has("logo_url") || has("logo_b64")} label="Logo azienda" />
          <StatusRow ok={has("firma_impresa_url") || has("firma_impresa_b64")} label="Firma+Timbro Azienda" />
          <StatusRow ok={has("firma_rt_url") || has("firma_rt_b64")} label="Firma+Timbro Resp. Tecnico" />
          <StatusRow ok={has("visura_url")} label="Visura Camerale (URL PDF)" />
          <StatusRow ok={has("visura_scadenza")} label="Data scadenza visura" />
          <StatusRow ok={has("cciaa_provincia") && has("rea_numero")} label="CCIAA + REA" />
        </div>
      )}

      {/* Anteprima Visura inline */}
      {impresa && has("visura_url") && (
        <VisuraPreview />
      )}

      {modalOpen && <ImpresaModal onClose={() => { setModalOpen(false); load(); }} />}
    </div>
  );
}


/**
 * Anteprima inline del PDF Visura Camerale collegato via URL nelle Impostazioni.
 * Chiama l'endpoint backend `/dico/impresa/visura/preview` che gestisce
 * automaticamente i link condivisi Aruba Drive/Nextcloud (aggiunge /download
 * quando necessario) e verifica i magic bytes PDF.
 */
function VisuraPreview() {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | testing | ok | error
  const [errorMsg, setErrorMsg] = useState("");
  const [blobUrl, setBlobUrl] = useState("");

  const testAndOpen = async () => {
    setStatus("testing");
    setErrorMsg("");
    try {
      const { data } = await api.get("/dico/impresa/visura/preview", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      setBlobUrl(url);
      setStatus("ok");
      setOpen(true);
    } catch (err) {
      const detail = err?.response?.data;
      let msg = "";
      if (detail instanceof Blob) {
        try { msg = JSON.parse(await detail.text()).detail || ""; } catch { msg = ""; }
      } else if (typeof detail === "object" && detail?.detail) {
        msg = detail.detail;
      }
      msg = msg || err?.message || "Errore sconosciuto";
      setStatus("error");
      setErrorMsg(msg);
      toast.error(`Anteprima Visura: ${msg}`);
    }
  };

  // Cleanup blob URL on unmount / new load
  useEffect(() => {
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blobUrl]);

  const iconColor = useMemo(() => {
    if (status === "ok") return "#059669";
    if (status === "error") return "#dc2626";
    return "var(--text-muted)";
  }, [status]);

  return (
    <div className="mt-4 pt-3 border-t" style={{ borderColor: "var(--border)" }} data-testid="visura-preview-block">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm">
          <FileCheck2 className="w-4 h-4" style={{ color: iconColor }} />
          <span className="font-semibold">Anteprima Visura Camerale</span>
          {status === "ok" && (
            <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: "#ecfdf5", color: "#065f46", border: "1px solid #a7f3d0" }} data-testid="visura-preview-ok">
              Link valido
            </span>
          )}
          {status === "error" && (
            <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: "#fef2f2", color: "#991b1b", border: "1px solid #fca5a5" }} data-testid="visura-preview-error">
              Link non valido
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={testAndOpen}
            disabled={status === "testing"}
            className="btn-secondary text-xs disabled:opacity-40"
            data-testid="visura-preview-test-btn">
            {status === "testing" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
            {open ? "Ricarica" : "Prova link e apri anteprima"}
          </button>
          {open && (
            <button onClick={() => setOpen(false)} className="btn-secondary text-xs" data-testid="visura-preview-close-btn">
              <EyeOff className="w-3.5 h-3.5" /> Chiudi
            </button>
          )}
        </div>
      </div>
      {status === "error" && errorMsg && (
        <div className="mt-2 text-xs p-2 rounded-md" style={{ background: "#fef2f2", color: "#991b1b" }} data-testid="visura-preview-error-msg">
          {errorMsg}
        </div>
      )}
      {open && blobUrl && (
        <div className="mt-3 rounded-md border overflow-hidden" style={{ borderColor: "var(--border)", height: 520 }} data-testid="visura-preview-iframe-wrapper">
          <iframe
            src={blobUrl}
            title="Anteprima Visura Camerale"
            className="w-full h-full"
            style={{ border: "none" }}
            data-testid="visura-preview-iframe"
          />
        </div>
      )}
    </div>
  );
}
